import { makeAutoObservable, runInAction } from 'mobx'
import { pick } from 'lodash'
import { decorate, inject, injectable } from '@evertel/di'
import { AsyncGetPartFormDataFn, AsyncOnPartCompleteFn, mediaMultipartUpload, MediaUploadModelName } from './mediaMultipartUploader'
import { Api } from '@evertel/api'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataMedia } from '@evertel/types'
import { NormalizedMedia } from './NormalizedMediaType'
import { localId } from '@evertel/utils'

export type UploadableMedia = NormalizedMedia & {
    computeContentLength?: () => Promise<number>
    onGetPartFormData: AsyncGetPartFormDataFn
    onPartComplete?: AsyncOnPartCompleteFn
}

export type AsyncOnUploadComplete = (mediaUploads: MediaUpload[], completedUpload: MediaUpload, error: any) => Promise<void>

type ModelType = 'room' | 'thread' | 'document'

export type MediaUpload = UploadableMedia & {
    modelId: number
    modelType: ModelType // TODO: add schema and document as types
    remoteMedia?: APIDataMedia
}

const modelTypeList: Record<ModelType, MediaUploadModelName> = {
    'room': 'RoomMessage',
    'thread': 'ThreadMessage',
    'document': 'Document'
}

class MediaUploadService {
    mediaUploads: MediaUpload[] = []

    constructor(
        private api: Api,
        private session: SessionService
    ) {
        makeAutoObservable(this)
    }

    addUploadableMedia = (modelId: number, modelType: ModelType, media: UploadableMedia) => {
        // negative numbers make sure id field does not collide with an actual remote data id
        // likely the localId() was already generated when the UploadableMedia was created so
        // not needed here.  That way the media keeps it's original localId()
        // and we can prevent duplicates since documents re-add files to queue
        const id = media.id ?? localId()

        // Remove existing entry with same id (if retrying)
        this.mediaUploads = this.mediaUploads.filter(m => m.id !== id)

        this.mediaUploads.push({
            ...media,
            modelId,
            modelType,
            state: 'readyToUpload',
            id
        })
    }

    removeMediaUpload = (mediaUpload: MediaUpload) => {
        this.mediaUploads = this.mediaUploads.filter(m => m.id !== mediaUpload.id)
    }

    clear = () => {
        this.mediaUploads = []
    }

    getMediaUploadsForModel = (modelId: number, modelType: ModelType): MediaUpload[] => {
        return this.mediaUploads.filter(m => m.modelId === modelId && m.modelType === modelType)
    }

    startMediaUploads = async (mediaUploads: MediaUpload[], onUploadComplete?: AsyncOnUploadComplete) => {
        const uploadableMedia = mediaUploads.filter(m => m.state === 'readyToUpload' || m.state === 'failed')
        const errors = []

        for (const uploadable of uploadableMedia) {
            try {
                runInAction(() => {
                    uploadable.state = 'uploading'
                })

                // Use meta from the uploadable media, falling back to picking root-level fields
                // for backwards compatibility with callers that don't set .meta
                const meta = uploadable.meta && Object.keys(uploadable.meta).length
                    ? uploadable.meta
                    : pick(uploadable, ['width', 'height', 'duration', 'orientation'])

                const remoteMedia = await mediaMultipartUpload(this.api, {
                    ...uploadable,
                    meta,
                    onProgress: (completed: number, total: number) => {
                        // Update observable progress
                        runInAction(() => {
                            uploadable.uploadProgress = (total > 0 && completed > 0) ? (completed / total) : 0
                        })
                    },
                    modelName: modelTypeList[uploadable?.modelType], // Model we are updating
                    modelId: uploadable.modelId, // Message id
                    userId: this.session.currentUserId,
                    deviceId: this.session.deviceId
                })

                // Media upload complete
                runInAction(() => {
                    // update local negative id so download tracking works on initial upload
                    uploadable.id = remoteMedia.id
                    uploadable.ownerId = remoteMedia.ownerId
                    uploadable.remoteMedia = remoteMedia
                    uploadable.uploadProgress = 1.0
                    uploadable.state = 'complete'
                })

                if (onUploadComplete) await onUploadComplete(uploadableMedia, uploadable, undefined)

            } catch (e) {
                // TODO: Handle this error somewhere.
                runInAction(() => {
                    uploadable.state = 'failed'
                    uploadable.uploadProgress = 0
                })
                errors.push(e)

                console.error(e)

                if (onUploadComplete) await onUploadComplete(uploadableMedia, uploadable, e)
            }
        }

        // If an error exists, we should throw it
        // We can either try to group the error messages, but only 1 can be thrown, the loop isn't possible
        // errors.forEach((err, idx) => {
        //     throw err
        // })
        if (errors.length) throw errors[0]

        return
    }
}

decorate(injectable(), MediaUploadService)
decorate(inject(Api), MediaUploadService, 0)
decorate(inject(SessionService), MediaUploadService, 1)

export { MediaUploadService }
